import React, { useEffect, useMemo, useRef, useState } from 'react';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import FilterMultiSelect from '../components/FilterMultiSelect';
import FocusPreferencesPanel from '../components/FocusPreferencesPanel';
import ReportsPanel from '../components/ReportsPanel';
import TaskCard from '../components/TaskCard';
import TaskCreateModal from '../components/TaskCreateModal';
import TaskDrawer from '../components/TaskDrawer';
import { buildWorkflowOptions, ensureAreaOption, ensureOption, formatStage } from '../data/team';
import { FOCUS_PAUSE_REASONS, buildFocusSessionSummary, formatTimerDuration, getFocusTimerSnapshot } from '../data/focusTimer';
import { DEFAULT_FOCUS_PREFERENCES, normalizeFocusPreferences } from '../data/focusPreferences';
import { compareFocusTasks, getTaskFocusSignals, isClosedLikeStage, isClosedLikeStatus } from '../data/taskFocus';
import { AuthAPI } from '../services/auth';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { SettingsAPI } from '../services/settings';
import { clearStoredFocusTimer, clearStoredFocusView, persistStoredFocusTimer, readStoredFocusTimer, readStoredSession } from '../services/session';
import { TasksAPI } from '../services/tasks';

const TASK_SORT_OPTIONS = [
  { value: 'lastMovementAt_desc', label: 'Última modificação mais recente' },
  { value: 'createdAt_desc', label: 'Data de criação mais recente' },
  { value: 'priority_desc', label: 'Prioridade mais alta primeiro' }
];

const FOCUS_PRESET_OPTIONS = [
  { value: 'all', label: 'Tudo' },
  { value: 'my_focus', label: 'Meu foco hoje' },
  { value: 'attention', label: 'Precisa de atenção' },
  { value: 'urgent', label: 'Urgentes' },
  { value: 'my_area', label: 'Da minha área' }
];

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function buildDefaultSelection(options, excludeMatcher = () => false) {
  const values = Array.isArray(options) ? options.filter(Boolean) : [];
  const filtered = values.filter((value) => !excludeMatcher(value));
  return filtered.length ? filtered : values;
}

function orderSelectedValues(selectedValues, availableValues) {
  const selectedSet = new Set(Array.isArray(selectedValues) ? selectedValues : []);
  return availableValues.filter((value) => selectedSet.has(value));
}

function alignSelectedValues(selectedValues, availableValues, fallbackValues) {
  if (!Array.isArray(selectedValues)) return [...fallbackValues];
  if (!selectedValues.length) return [];

  const orderedValues = orderSelectedValues(selectedValues, availableValues);
  return orderedValues.length ? orderedValues : [...fallbackValues];
}

function serializeFilterSelection(selectedValues, allValues) {
  const orderedValues = orderSelectedValues(selectedValues, allValues);
  if (!orderedValues.length) return '__none__';
  if (orderedValues.length === allValues.length) return undefined;
  return orderedValues.join(',');
}

function resolveUserAreaCodes(user) {
  const source = Array.isArray(user?.areas) ? user.areas : [];
  return source
    .map((area) => (typeof area === 'string' ? area : area?.code))
    .map((code) => String(code || '').trim())
    .filter(Boolean);
}

function initialFilters(currentUser, workflowOptions, focusPreferences = DEFAULT_FOCUS_PREFERENCES) {
  return {
    statuses: buildDefaultSelection(workflowOptions?.statuses, isClosedLikeStatus),
    stages: buildDefaultSelection(workflowOptions?.stages, isClosedLikeStage),
    priorities: Array.isArray(workflowOptions?.priorities) ? [...workflowOptions.priorities] : [],
    areas: Array.isArray(workflowOptions?.areas) ? workflowOptions.areas.map((area) => area.code) : [],
    assignees: currentUser?.id ? [currentUser.id] : [],
    q: '',
    sort: focusPreferences.defaultSort || 'lastMovementAt_desc'
  };
}

export default function Tarefas({ currentUser, onLogout, onSessionUserUpdated }) {
  const userFocusPreferences = useMemo(
    () => normalizeFocusPreferences(currentUser?.focusPreferences),
    [currentUser]
  );
  const activeViewSessionRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [kpis, setKpis] = useState({ openCount: 0, stalledCount: 0, perUserMs: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drawerTask, setDrawerTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [workflowSettings, setWorkflowSettings] = useState(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [showFocusPanel, setShowFocusPanel] = useState(false);
  const [showReportsPanel, setShowReportsPanel] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [activeFocusPreset, setActiveFocusPreset] = useState(userFocusPreferences.defaultPreset || 'all');
  const [focusedTaskId, setFocusedTaskId] = useState('');
  const workflowOptions = useMemo(() => buildWorkflowOptions(workflowSettings || {}), [workflowSettings]);
  const stageOptions = useMemo(() => ensureOption(workflowOptions.stages, ''), [workflowOptions]);
  const statusOptions = useMemo(() => ensureOption(workflowOptions.statuses, ''), [workflowOptions]);
  const priorityOptions = useMemo(() => ensureOption(workflowOptions.priorities, ''), [workflowOptions]);
  const areaOptions = useMemo(() => ensureAreaOption(workflowOptions.areas, null), [workflowOptions]);
  const [filters, setFilters] = useState(() => initialFilters(currentUser, buildWorkflowOptions(), userFocusPreferences));
  const [focusTimer, setFocusTimer] = useState(null);
  const [timerNow, setTimerNow] = useState(() => Date.now());

  const userOptions = useMemo(() => {
    const list = [...users];
    if (currentUser?.id && !list.some((user) => user.id === currentUser.id)) list.unshift(currentUser);
    return list;
  }, [users, currentUser]);

  const canAdminister = currentUser?.role === 'admin';
  const statusFilterOptions = useMemo(
    () => statusOptions.map((status) => ({ value: status, label: status })),
    [statusOptions]
  );
  const stageFilterOptions = useMemo(
    () => stageOptions.map((stage) => ({ value: stage, label: formatStage(stage) })),
    [stageOptions]
  );
  const priorityFilterOptions = useMemo(
    () => priorityOptions.map((priority) => ({ value: priority, label: priority })),
    [priorityOptions]
  );
  const areaFilterOptions = useMemo(
    () => areaOptions.map((area) => ({ value: area.code, label: area.name })),
    [areaOptions]
  );
  const assigneeFilterOptions = useMemo(
    () => userOptions.map((user) => ({ value: user.id, label: user.name })),
    [userOptions]
  );
  const assigneeFilterSummary = useMemo(() => {
    const allAssigneeIds = assigneeFilterOptions.map((option) => option.value);
    const selectedAssignees = orderSelectedValues(filters.assignees, allAssigneeIds);
    if (!selectedAssignees.length) return 'Nenhum responsável';
    if (currentUser?.id && selectedAssignees.length === 1 && selectedAssignees[0] === currentUser.id) return 'Minhas tarefas';
    if (selectedAssignees.length === allAssigneeIds.length) return 'Operação geral';
    return 'Equipe filtrada';
  }, [assigneeFilterOptions, currentUser, filters.assignees]);
  const userAreaCodes = useMemo(() => resolveUserAreaCodes(currentUser), [currentUser]);
  const focusPresetOptions = useMemo(
    () => [
      ...FOCUS_PRESET_OPTIONS,
      ...(userFocusPreferences.customPresets || []).map((preset) => ({
        value: preset.id,
        label: preset.label,
        custom: true
      }))
    ],
    [userFocusPreferences.customPresets]
  );
  const openTasks = useMemo(
    () => tasks.filter((task) => !isClosedLikeStatus(task.status)),
    [tasks]
  );
  const attentionTasks = useMemo(
    () => [...openTasks]
      .filter((task) => {
        const focus = getTaskFocusSignals(task, currentUser?.id || '', userFocusPreferences);
        return focus.overdue || focus.stalenessLevel > 0 || focus.needsAssignee || focus.isUrgent;
      })
      .sort((a, b) => compareFocusTasks(a, b, currentUser?.id || '', userFocusPreferences)),
    [currentUser, openTasks, userFocusPreferences]
  );
  const displayedTasks = useMemo(() => {
    const rows = [...tasks];
    const customPreset = (userFocusPreferences.customPresets || []).find((preset) => preset.id === activeFocusPreset);

    if (customPreset) {
      const query = normalizeSearchText(customPreset.q);
      return rows
        .filter((task) => {
          const focus = getTaskFocusSignals(task, currentUser?.id || '', userFocusPreferences);
          if (focus.closed) return false;
          if (customPreset.attentionOnly && !(focus.overdue || focus.stalenessLevel > 0 || focus.needsAssignee || focus.isUrgent || focus.dueSoon)) return false;
          if (customPreset.assigneeMode === 'me' && task.assignee?.id !== currentUser?.id) return false;
          if (customPreset.assigneeMode === 'unassigned' && task.assignee?.id) return false;
          if (customPreset.statuses?.length && !customPreset.statuses.includes(task.status)) return false;
          if (customPreset.stages?.length && !customPreset.stages.includes(task.stage)) return false;
          if (customPreset.priorities?.length && !customPreset.priorities.includes(task.priority)) return false;
          if (customPreset.areas?.length && !customPreset.areas.includes(task.area?.code)) return false;
          if (query) {
            const haystack = normalizeSearchText([
              task.title,
              task.description,
              task.customer?.name,
              task.assignee?.name,
              task.area?.name,
              task.lastActivity?.summary
            ].filter(Boolean).join(' '));
            if (!haystack.includes(query)) return false;
          }
          return true;
        })
        .sort((a, b) => compareFocusTasks(a, b, currentUser?.id || '', userFocusPreferences));
    }

    switch (activeFocusPreset) {
      case 'my_focus':
        return rows
          .filter((task) => !isClosedLikeStatus(task.status) && task.assignee?.id === currentUser?.id)
          .sort((a, b) => compareFocusTasks(a, b, currentUser?.id || '', userFocusPreferences));
      case 'attention':
        return attentionTasks;
      case 'urgent':
        return rows
          .filter((task) => {
            const focus = getTaskFocusSignals(task, currentUser?.id || '', userFocusPreferences);
            return !focus.closed && focus.isHighPriority;
          })
          .sort((a, b) => compareFocusTasks(a, b, currentUser?.id || '', userFocusPreferences));
      case 'my_area':
        if (!userAreaCodes.length) return [];
        return rows
          .filter((task) => userAreaCodes.includes(task.area?.code))
          .sort((a, b) => compareFocusTasks(a, b, currentUser?.id || '', userFocusPreferences));
      case 'all':
      default:
        return rows;
    }
  }, [activeFocusPreset, attentionTasks, currentUser, tasks, userAreaCodes, userFocusPreferences]);
  const focusedTask = useMemo(
    () => tasks.find((task) => task._id === focusedTaskId) || null,
    [focusedTaskId, tasks]
  );
  const isFocusMode = activeFocusPreset !== 'all';
  const renderedTasks = focusedTask ? [focusedTask] : displayedTasks;
  const focusTimerSnapshot = useMemo(() => getFocusTimerSnapshot(focusTimer, timerNow), [focusTimer, timerNow]);

  const handleApiError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onLogout();
      return true;
    }

    setError(getErrorMessage(requestError, fallbackMessage));
    return false;
  };

  const closeActiveTaskView = async (reason = 'close') => {
    const activeSession = activeViewSessionRef.current;
    if (!activeSession?.taskId || !activeSession?.sessionId) return null;

    activeViewSessionRef.current = null;

    try {
      const payload = await TasksAPI.endViewSession(activeSession.taskId, {
        sessionId: activeSession.sessionId,
        reason
      });
      if (payload?.task) {
        setTasks((current) => current.map((task) => (task._id === payload.task._id ? payload.task : task)));
      }
      return payload;
    } catch (_error) {
      return null;
    }
  };

  const startTaskView = async (task) => {
    if (!task?._id) return;
    const activeSession = activeViewSessionRef.current;
    if (activeSession?.taskId === task._id && activeSession?.sessionId) return;

    await closeActiveTaskView('switch_task');

    try {
      const payload = await TasksAPI.startViewSession(task._id);
      if (payload?.sessionId) {
        activeViewSessionRef.current = {
          taskId: task._id,
          sessionId: payload.sessionId
        };
      }
      if (payload?.task) {
        setTasks((current) => current.map((row) => (row._id === payload.task._id ? payload.task : row)));
        setDrawerTask((current) => (current?._id === payload.task._id ? payload.task : current));
      }
    } catch (requestError) {
      handleApiError(requestError, 'A tarefa foi aberta, mas não consegui registrar a análise.');
    }
  };

  const closeActiveTaskViewOnUnload = () => {
    const activeSession = activeViewSessionRef.current;
    if (!activeSession?.taskId || !activeSession?.sessionId) return;

    const session = readStoredSession();
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api-task.fly.dev';
    const url = `${baseUrl.replace(/\/+$/, '')}/tasks/${activeSession.taskId}/view-sessions/end`;

    try {
      fetch(url, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {})
        },
        body: JSON.stringify({
          sessionId: activeSession.sessionId,
          reason: 'page_unload'
        })
      });
    } catch (_error) {
      // Best effort para não travar a saída da página.
    }
  };

  const load = async (activeFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(Object.entries({
        q: activeFilters.q.trim(),
        sort: activeFilters.sort,
        status: serializeFilterSelection(activeFilters.statuses, statusOptions),
        stage: serializeFilterSelection(activeFilters.stages, stageOptions),
        priority: serializeFilterSelection(activeFilters.priorities, priorityOptions),
        area: serializeFilterSelection(activeFilters.areas, areaOptions.map((area) => area.code)),
        assignee: serializeFilterSelection(activeFilters.assignees, userOptions.map((user) => user.id))
      }).filter(([, value]) => typeof value !== 'undefined' && value !== ''));
      const [taskData, kpiData] = await Promise.all([TasksAPI.list(params), TasksAPI.kpis()]);
      setTasks(taskData);
      setKpis(kpiData);
      if (drawerTask?._id) {
        const updatedTask = taskData.find((task) => task._id === drawerTask._id);
        if (updatedTask) setDrawerTask(updatedTask);
      }
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar as tarefas. Verifique se a API está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const rows = await AuthAPI.listUsers();
      setUsers(rows);
    } catch (requestError) {
      if (handleApiError(requestError, 'Não foi possível carregar os usuários.')) return;
      setUsers(currentUser ? [currentUser] : []);
    }
  };

  const loadWorkflow = async () => {
    try {
      const settings = await SettingsAPI.workflow();
      setWorkflowSettings(settings);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar as configurações operacionais.');
    }
  };

  useEffect(() => {
    void loadUsers();
    void loadWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canAdminister) {
      setShowSettingsPanel(false);
    }
  }, [canAdminister]);

  useEffect(() => {
    const handleGlobalEscape = (event) => {
      if (event.key !== 'Escape') return;

      setShowFiltersPanel(false);
      setShowTaskPanel(false);
      setShowFocusPanel(false);
      setShowReportsPanel(false);
      setShowSettingsPanel(false);
      void closeActiveTaskView('escape');
      setDrawerTask(null);
      setShowCreateModal(false);
    };

    document.addEventListener('keydown', handleGlobalEscape);
    return () => document.removeEventListener('keydown', handleGlobalEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', closeActiveTaskViewOnUnload);
    return () => {
      closeActiveTaskViewOnUnload();
      window.removeEventListener('beforeunload', closeActiveTaskViewOnUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFocusedTaskId('');
  }, [activeFocusPreset]);

  useEffect(() => {
    const nextFilters = initialFilters(currentUser, buildWorkflowOptions(), userFocusPreferences);
    clearStoredFocusView(currentUser?.id);
    setActiveFocusPreset(userFocusPreferences.defaultPreset || 'all');
    setFilters(nextFilters);
    setFocusedTaskId('');
    void load(nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    const storedTimer = readStoredFocusTimer(currentUser?.id);
    setFocusTimer(storedTimer);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!focusTimer) return undefined;

    setTimerNow(Date.now());
    const intervalId = window.setInterval(() => setTimerNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [focusTimer]);

  useEffect(() => {
    if (!currentUser?.id) return;
    if (focusTimer) persistStoredFocusTimer(currentUser.id, focusTimer);
    else clearStoredFocusTimer(currentUser.id);
  }, [currentUser?.id, focusTimer]);

  useEffect(() => {
    const originalTitle = document.title;
    if (!focusTimer) return () => { document.title = originalTitle; };

    const timeLabel = focusTimerSnapshot.cycleCompleted
      ? 'Bloco concluído'
      : focusTimerSnapshot.isActive
        ? formatTimerDuration(focusTimerSnapshot.isRunning && focusTimer.durationMinutes > 0 ? focusTimerSnapshot.remainingMs : focusTimerSnapshot.elapsedMs)
        : '';

    document.title = `${focusTimerSnapshot.isPaused ? 'Pausado' : 'Foco'} • ${timeLabel} • ${focusTimer.taskTitle}`;

    return () => {
      document.title = originalTitle;
    };
  }, [focusTimer, focusTimerSnapshot]);

  useEffect(() => {
    const defaultStatuses = buildDefaultSelection(statusOptions, isClosedLikeStatus);
    const defaultStages = buildDefaultSelection(stageOptions, isClosedLikeStage);
    const allAreas = areaOptions.map((area) => area.code);
    const allAssignees = userOptions.map((user) => user.id);
    const defaultAssignees = currentUser?.id && allAssignees.includes(currentUser.id) ? [currentUser.id] : allAssignees;

    setFilters((current) => ({
      ...current,
      statuses: alignSelectedValues(current.statuses, statusOptions, defaultStatuses),
      stages: alignSelectedValues(current.stages, stageOptions, defaultStages),
      priorities: alignSelectedValues(current.priorities, priorityOptions, priorityOptions),
      areas: alignSelectedValues(current.areas, allAreas, allAreas),
      assignees: alignSelectedValues(current.assignees, allAssignees, defaultAssignees),
      sort: current.sort || userFocusPreferences.defaultSort || 'lastMovementAt_desc'
    }));
  }, [stageOptions, statusOptions, priorityOptions, areaOptions, userOptions, currentUser, userFocusPreferences]);

  const handleCreate = async (payload) => {
    setLoading(true);
    setError('');
    try {
      await TasksAPI.create(payload);
      setShowCreateModal(false);
      await load(filters);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar a tarefa.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async (event) => {
    event.preventDefault();
    await load(filters);
    setShowFiltersPanel(false);
  };

  const clearFilters = async () => {
    const nextFilters = initialFilters(currentUser, workflowOptions, userFocusPreferences);
    setFilters(nextFilters);
    await load(nextFilters);
    setShowFiltersPanel(false);
  };

  const handleTaskUpdated = async (updatedTask) => {
    setDrawerTask(updatedTask);
    if (focusedTaskId === updatedTask?._id) setFocusedTaskId(updatedTask._id);
    await load(filters);
  };

  const handleTaskDeleted = async (task) => {
    await closeActiveTaskView('delete_task');
    await TasksAPI.remove(task._id);
    if (focusTimer?.taskId === task._id) {
      setFocusTimer(null);
    }
    setDrawerTask(null);
    await load(filters);
  };

  const handleSaveWorkflow = async (payload) => {
    const saved = await SettingsAPI.saveWorkflow(payload);
    setWorkflowSettings(saved);
    return saved;
  };

  const handleSaveUser = async (code, payload) => {
    const updated = await AuthAPI.updateUser(code, payload);
    setUsers((current) => current.map((user) => (user.code === code ? updated : user)));
    if (updated.code === currentUser?.code) {
      onSessionUserUpdated?.(updated);
    }
    return updated;
  };

  const handleSaveFocusPreferences = async (preferences) => {
    const { user } = await AuthAPI.updateMyFocusPreferences(preferences);
    const savedPreferences = normalizeFocusPreferences(user.focusPreferences);
    const nextFilters = initialFilters(user, workflowOptions, savedPreferences);

    onSessionUserUpdated?.(user);
    setActiveFocusPreset(savedPreferences.defaultPreset || 'all');
    setFilters(nextFilters);
    setFocusedTaskId('');
    await load(nextFilters);

    return user;
  };

  const upsertTaskLocally = (updatedTask) => {
    setTasks((current) => current.map((task) => (task._id === updatedTask._id ? updatedTask : task)));
    if (drawerTask?._id === updatedTask._id) setDrawerTask(updatedTask);
  };

  const handleStartFocusTimer = (task, durationMinutes = 25) => {
    if (!task?._id) return;
    if (focusTimer?.taskId && focusTimer.taskId !== task._id) {
      const confirmed = window.confirm(`Já existe uma sessão de foco em "${focusTimer.taskTitle}". Deseja trocar para "${task.title}"?`);
      if (!confirmed) return;
    }

    setFocusTimer({
      taskId: task._id,
      taskTitle: task.title,
      startedAt: Date.now(),
      lastResumedAt: Date.now(),
      accumulatedMs: 0,
      durationMinutes: Number(durationMinutes) || 0,
      status: 'running',
      pauses: [],
      pausedAt: null,
      pauseReason: ''
    });
    setDrawerTask(task);
  };

  const handlePauseFocusTimer = (reason = 'Interrupção') => {
    setFocusTimer((current) => {
      if (!current || current.status !== 'running') return current;
      const now = Date.now();
      return {
        ...current,
        accumulatedMs: (Number(current.accumulatedMs) || 0) + Math.max(now - Number(current.lastResumedAt || now), 0),
        status: 'paused',
        pausedAt: now,
        pauseReason: String(reason || 'Interrupção').trim() || 'Interrupção'
      };
    });
  };

  const handleResumeFocusTimer = () => {
    setFocusTimer((current) => {
      if (!current || current.status !== 'paused') return current;
      const now = Date.now();
      const nextPauses = [...(Array.isArray(current.pauses) ? current.pauses : [])];

      if (current.pausedAt) {
        nextPauses.push({
          reason: current.pauseReason || 'Interrupção',
          startedAt: current.pausedAt,
          endedAt: now,
          durationMs: Math.max(now - Number(current.pausedAt), 0)
        });
      }

      return {
        ...current,
        status: 'running',
        lastResumedAt: now,
        pausedAt: null,
        pauseReason: '',
        pauses: nextPauses
      };
    });
  };

  const handleFinishFocusTimer = async ({ cancel = false } = {}) => {
    if (!focusTimer) return;

    const now = Date.now();
    const timerToFinish = focusTimer.status === 'paused'
      ? {
          ...focusTimer,
          pauses: focusTimer.pausedAt
            ? [
                ...(Array.isArray(focusTimer.pauses) ? focusTimer.pauses : []),
                {
                  reason: focusTimer.pauseReason || 'Interrupção',
                  startedAt: focusTimer.pausedAt,
                  endedAt: now,
                  durationMs: Math.max(now - Number(focusTimer.pausedAt), 0)
                }
              ]
            : focusTimer.pauses
        }
      : {
          ...focusTimer,
          accumulatedMs: getFocusTimerSnapshot(focusTimer, now).elapsedMs
        };

    setFocusTimer(null);

    if (cancel) return;

    try {
      const updated = await TasksAPI.comment(
        timerToFinish.taskId,
        buildFocusSessionSummary(timerToFinish, currentUser?.name)
      );
      upsertTaskLocally(updated);
    } catch (requestError) {
      handleApiError(requestError, 'A sessão de foco foi encerrada, mas não consegui registrar o resumo na timeline.');
    }
  };

  const handleOpenTask = (task) => {
    if (isFocusMode && userFocusPreferences.isolateOnOpen) {
      setFocusedTaskId(task._id);
    }
    setDrawerTask(task);
    void startTaskView(task);
  };

  const handleCloseDrawer = () => {
    void closeActiveTaskView('close_drawer');
    setDrawerTask(null);
  };

  const handlePresetChange = (preset) => {
    setActiveFocusPreset(preset);
  };

  return (
    <div className="app-shell">
      <section className="task-home-toolbar">
        <div className="task-home-toolbar__title">
          <div className="eyebrow">Tarefas</div>
          <h1>Rotina operacional</h1>
        </div>

        <div className="header-kpis" aria-label="Indicadores principais">
          <div className="header-kpi">
            <span>Abertas ou em andamento</span>
            <strong>{kpis.openCount}</strong>
          </div>
          <div className="header-kpi">
            <span>Sem movimentação &gt; 24h</span>
            <strong>{kpis.stalledCount}</strong>
          </div>
          <div className="header-kpi header-kpi--wide">
            <span>Filtro atual</span>
            <strong>{assigneeFilterSummary}</strong>
          </div>
        </div>

        <div className="task-home-toolbar__actions">
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowCreateModal(true)}>Nova tarefa</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowTaskPanel(true)}>Painel</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowFocusPanel(true)}>Foco</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowReportsPanel(true)}>Relatórios</button>
          {canAdminister && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowSettingsPanel(true)}>Configurações</button>}
        </div>
      </section>

      {focusTimer && (
        <section className={`focus-timer-banner ${focusTimerSnapshot.isPaused ? 'focus-timer-banner--paused' : focusTimerSnapshot.cycleCompleted ? 'focus-timer-banner--done' : 'focus-timer-banner--running'} mt-4`}>
          <div>
            <div className="eyebrow">Foco ativo</div>
            <h3 className="mb-1">{focusTimer.taskTitle}</h3>
            <div className="small">
              {focusTimer.durationMinutes > 0
                ? focusTimerSnapshot.cycleCompleted
                  ? `Bloco concluído. Tempo total: ${formatTimerDuration(focusTimerSnapshot.elapsedMs)}`
                  : focusTimerSnapshot.isPaused
                    ? `Pausado em ${formatTimerDuration(focusTimerSnapshot.elapsedMs)}`
                    : `Restante: ${formatTimerDuration(focusTimerSnapshot.remainingMs)}`
                : focusTimerSnapshot.isPaused
                  ? `Pausado em ${formatTimerDuration(focusTimerSnapshot.elapsedMs)}`
                  : `Tempo em foco: ${formatTimerDuration(focusTimerSnapshot.elapsedMs)}`}
            </div>
          </div>

          <div className="focus-timer-banner__actions">
            <button type="button" className="btn btn-sm btn-outline-light" onClick={() => {
              const timerTask = tasks.find((task) => task._id === focusTimer.taskId);
              if (timerTask) handleOpenTask(timerTask);
            }}>
              Abrir tarefa
            </button>
            {focusTimerSnapshot.isRunning ? (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => handlePauseFocusTimer(window.prompt('Motivo da pausa', FOCUS_PAUSE_REASONS[0]) || FOCUS_PAUSE_REASONS[0])}
              >
                Pausar
              </button>
            ) : (
              <button type="button" className="btn btn-sm btn-light" onClick={handleResumeFocusTimer}>
                Retomar
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-light" onClick={() => handleFinishFocusTimer()}>
              Encerrar
            </button>
          </div>
        </section>
      )}

      {error && <div className="alert alert-danger mt-4">{error}</div>}

      <main className="main-task-surface mt-4">
        <section className="task-list-panel">
          <div className="task-list-header">
            <div>
              <h2>Tarefas</h2>
              {focusedTask && <p className="text-muted mb-0">Tarefa isolada no foco.</p>}
            </div>
            <div className="focus-preset-group task-header-presets">
              {focusPresetOptions.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`btn btn-sm ${activeFocusPreset === preset.value ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => handlePresetChange(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="task-list-header-actions">
              {focusedTask && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFocusedTaskId('')}>
                  Voltar para a lista
                </button>
              )}
              <button type="button" className="btn btn-outline-dark" onClick={() => setShowFiltersPanel(true)}>
                Filtros
              </button>
              <span className="task-list-counter">{renderedTasks.length} tarefas</span>
            </div>
          </div>

          <div className="task-list mt-4">
            {renderedTasks.map((task) => (
              <TaskCard key={task._id} task={task} onOpen={handleOpenTask} currentUser={currentUser} focusPreferences={userFocusPreferences} isFocusTracked={focusTimer?.taskId === task._id} />
            ))}
            {!renderedTasks.length && !loading && (
              <div className="empty-state">
                {activeFocusPreset === 'my_area' && !userAreaCodes.length
                  ? 'Seu usuário ainda não está vinculado a nenhuma área.'
                  : 'Nenhuma tarefa encontrada com a visão atual.'}
              </div>
            )}
            {loading && <div className="empty-state">Carregando tarefas...</div>}
          </div>
        </section>
      </main>

      {showFiltersPanel && (
        <div className="drawer-backdrop drawer-backdrop--right">
          <aside className="task-overlay-panel task-overlay-panel--narrow shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Filtros</div>
                <h2>Filtrar tarefas</h2>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowFiltersPanel(false)}>Fechar</button>
            </div>

            <form className="filters-grid filters-grid--panel mt-3" onSubmit={applyFilters}>
              <div className="filter-control filter-search-field">
                <label className="form-label">Busca</label>
                <input
                  className="form-control"
                  value={filters.q}
                  onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
                  placeholder="Buscar por título, descrição, cliente, área ou responsável"
                />
              </div>

              <div className="filter-control">
                <label className="form-label">Ordenar por</label>
                <select className="form-select" value={filters.sort} onChange={(event) => setFilters((current) => ({ ...current, sort: event.target.value }))}>
                  {TASK_SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>

              <FilterMultiSelect
                label="Status"
                options={statusFilterOptions}
                selectedValues={filters.statuses}
                onChange={(values) => setFilters((current) => ({ ...current, statuses: values }))}
                allLabel="Todos os status"
                noneLabel="Nenhum status"
              />

              <FilterMultiSelect
                label="Etapas"
                options={stageFilterOptions}
                selectedValues={filters.stages}
                onChange={(values) => setFilters((current) => ({ ...current, stages: values }))}
                allLabel="Todas as etapas"
                noneLabel="Nenhuma etapa"
              />

              <FilterMultiSelect
                label="Prioridades"
                options={priorityFilterOptions}
                selectedValues={filters.priorities}
                onChange={(values) => setFilters((current) => ({ ...current, priorities: values }))}
                allLabel="Todas as prioridades"
                noneLabel="Nenhuma prioridade"
              />

              <FilterMultiSelect
                label="Áreas"
                options={areaFilterOptions}
                selectedValues={filters.areas}
                onChange={(values) => setFilters((current) => ({ ...current, areas: values }))}
                allLabel="Todas as áreas"
                noneLabel="Nenhuma área"
              />

              <FilterMultiSelect
                label="Responsáveis"
                options={assigneeFilterOptions}
                selectedValues={filters.assignees}
                onChange={(values) => setFilters((current) => ({ ...current, assignees: values }))}
                allLabel="Todos os responsáveis"
                noneLabel="Nenhum responsável"
              />

              <div className="overlay-actions">
                <button className="btn btn-dark" type="submit">Aplicar filtros</button>
                <button className="btn btn-outline-secondary" type="button" onClick={clearFilters}>Limpar filtros</button>
              </div>
            </form>
            <div className="filters-hint mt-3">Status e etapas concluídos começam desmarcados para manter o foco no que ainda precisa de atenção.</div>
          </aside>
        </div>
      )}

      {showTaskPanel && (
        <div className="drawer-backdrop drawer-backdrop--right">
          <aside className="task-overlay-panel shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Operação</div>
                <h2>Painel de tarefas</h2>
                <p className="text-muted mb-0">Abra uma tarefa para atualizar o log, anexar vínculos, ver a timeline completa e passar para o próximo responsável.</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowTaskPanel(false)}>Fechar</button>
            </div>

            <div className="overlay-actions mt-3">
              <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>Nova tarefa</button>
              <button type="button" className="btn btn-outline-primary" onClick={() => load()} disabled={loading}>Atualizar painel</button>
            </div>

            <div className="compact-panel-grid mt-4">
              <div className="header-kpi">
                <span>Abertas ou em andamento</span>
                <strong>{kpis.openCount}</strong>
              </div>
              <div className="header-kpi">
                <span>Sem movimentação &gt; 24h</span>
                <strong>{kpis.stalledCount}</strong>
              </div>
              <div className="header-kpi">
                <span>Filtro atual</span>
                <strong>{assigneeFilterSummary}</strong>
              </div>
            </div>
          </aside>
        </div>
      )}

      {showFocusPanel && (
        <div className="drawer-backdrop drawer-backdrop--right">
          <aside className="task-overlay-panel shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Modo foco</div>
                <h2>Preferências de foco</h2>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowFocusPanel(false)}>Fechar</button>
            </div>

            <details className="focus-extra-panel mt-3" open>
              <summary>
                <span>Preferências e alertas</span>
                <strong>{attentionTasks.length} em atenção</strong>
              </summary>

              <FocusPreferencesPanel
                preferences={userFocusPreferences}
                statusOptions={statusFilterOptions}
                stageOptions={stageFilterOptions}
                priorityOptions={priorityFilterOptions}
                areaOptions={areaFilterOptions}
                sortOptions={TASK_SORT_OPTIONS}
                presetOptions={FOCUS_PRESET_OPTIONS}
                customPresetOptions={focusPresetOptions}
                onSave={handleSaveFocusPreferences}
              />
            </details>
          </aside>
        </div>
      )}

      {showReportsPanel && (
        <div className="drawer-backdrop drawer-backdrop--right">
          <ReportsPanel
            onClose={() => setShowReportsPanel(false)}
            onUnauthorized={onLogout}
          />
        </div>
      )}

      {showSettingsPanel && canAdminister && (
        <div className="drawer-backdrop drawer-backdrop--right">
          <aside className="task-overlay-panel task-overlay-panel--wide shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Administração</div>
                <h2>Configurações</h2>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSettingsPanel(false)}>Fechar</button>
            </div>
            <AdminSettingsPanel
              workflowSettings={workflowSettings || workflowOptions}
              users={userOptions}
              onSaveWorkflow={handleSaveWorkflow}
              onSaveUser={handleSaveUser}
            />
          </aside>
        </div>
      )}

      {showCreateModal && (
        <TaskCreateModal
          currentUser={currentUser}
          users={userOptions}
          workflowOptions={workflowOptions}
          loading={loading}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}

      <TaskDrawer
        task={drawerTask}
        currentUser={currentUser}
        users={userOptions}
        workflowOptions={workflowOptions}
        focusTimer={focusTimer}
        focusTimerSnapshot={focusTimerSnapshot}
        onStartFocusTimer={handleStartFocusTimer}
        onPauseFocusTimer={handlePauseFocusTimer}
        onResumeFocusTimer={handleResumeFocusTimer}
        onFinishFocusTimer={handleFinishFocusTimer}
        canDelete={canAdminister}
        onDeleteTask={handleTaskDeleted}
        onClose={handleCloseDrawer}
        onTaskUpdated={handleTaskUpdated}
        onUnauthorized={onLogout}
      />
    </div>
  );
}
